import React from "react";
import ProgramTemplate from "@/components/programs/ProgramTemplate";

const ProfessionalConsultation = () => (
  <ProgramTemplate
    pageKey="professional-consultation"
    breadcrumbLabel="Professional Consultation"
    overline="Program 03 · Professional Consultation"
    headline="For clinicians and practitioners"
    headlineAccent="applying the methodology in their own work."
    audience="For healthcare professionals, coaches, and practitioners"
    lead="Professional Consultation supports clinicians, coaches, and healthcare professionals who want to integrate Adaptive Physiology™ into the practice they already run — through case consultation, framework education, and structured collaboration."
    loopHighlight="feedback"
    whyExists={{
      title: "A methodology is only as durable as the practitioners who can carry it.",
      paragraphs: [
        "Adaptive Physiology™ is designed to be taught, applied, and adapted by qualified professionals inside their own settings — not to be locked inside a single practice.",
        "Professional Consultation is the current pathway for that: a structured way for clinicians, coaches, and practitioners to bring the framework into their existing work, with support from the team that developed it.",
      ],
    }}
    howApplied={{
      title: "Framework fluency, applied to your practice.",
      intro:
        "The loop is examined at the level of professional practice — how a clinician or coach delivers each movement inside their existing model of care.",
      steps: [
        {
          title: "Practice assessment",
          body: "A structured review of the practitioner’s current model, populations, and where Adaptive Physiology™ most cleanly integrates.",
        },
        {
          title: "Framework education & prescription patterns",
          body: "Deep-dive on the four pillars and how each is translated into prescription within the practitioner’s scope.",
        },
        {
          title: "Case consultation & reinforcement",
          body: "Ongoing consultation on live cases; refinement of protocols and signal delivery inside the practitioner’s workflow.",
        },
        {
          title: "Collaborative adaptation",
          body: "Reassessment of the practitioner’s implementation over time — protocols evolve as the practice matures.",
        },
      ],
    }}
    experience={{
      title: "Professional development that changes what you can offer.",
      intro:
        "The goal is not brand licensing. The goal is genuine fluency — a practitioner who can deliver Adaptive Physiology™ with integrity inside their own work.",
      capacities: [
        { label: "Framework fluency", body: "Comfortable delivering all four movements within your scope of practice." },
        { label: "Case-based judgment", body: "Sound clinical reasoning about state, stimulus, and adaptation across populations." },
        { label: "Integrative capacity", body: "Ability to fold Adaptive Physiology™ into your existing model without collision." },
        { label: "Assessment literacy", body: "Working comfort with HRV, autonomic markers, and behavioral signal interpretation." },
        { label: "Adaptive prescription", body: "Skill in dosing stimulus precisely for the state in front of you." },
        { label: "Collegial resource", body: "Direct access to consultation on the cases that most benefit from it." },
      ],
    }}
    designedFor={{
      title: "Qualified professionals in adjacent fields.",
      groups: [
        { title: "Clinicians", body: "Physicians, nurse practitioners, PAs, and clinical psychologists incorporating physiological work." },
        { title: "Therapists", body: "Psychotherapists and counselors adding a physiological layer beneath narrative work." },
        { title: "Recovery professionals", body: "Addiction counselors and recovery program staff seeking a physiological framework." },
        { title: "Coaches", body: "Health, performance, and behavior-change coaches with a scientific orientation." },
        { title: "Rehabilitation practitioners", body: "PTs, chiropractors, and exercise physiologists working with regulation, not just tissue." },
        { title: "Educators", body: "Faculty and clinical educators building curriculum around behavior change and physiology." },
      ],
    }}
    nextSteps={{
      primary: {
        label: "Open a professional conversation",
        desc: "A short intake helps us understand your practice and how consultation might fit.",
        to: "/apply",
      },
      secondary: {
        label: "Explore Organizational Partnerships",
        desc: "If you represent an organization considering methodology integration at scale.",
        to: "/programs/organizational-partnerships",
      },
    }}
  />
);
export default ProfessionalConsultation;
